import { SafetyControlSurveyView } from "src/api/generated/erp/db/types/tables/safetyControlSurveyView.ts";
import { SafetyControlSurveyQueryApi } from "src/api/generated/erp/safetyControlSurvey/api/safetyControlSurveyQueryApi.ts";
import { SafetyControlSurveyEditApi } from "src/api/generated/erp/safetyControlSurvey/api/safetyControlSurveyEditApi.ts";
import { SafetyControlSurveyActionApi } from "src/api/generated/erp/safetyControlSurvey/api/safetyControlSurveyActionApi.ts";
import { SafetyControlSurveyReportApi } from "src/api/generated/erp/safetyControlSurvey/api/safetyControlSurveyReportApi.ts";
import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { FormCrudDataGrid } from "src/components/common/dataGrid/crud/FormCrudDataGrid.tsx";
import { dateTimeColumn, enumColumn, integerColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { RefreshableElementRef } from "src/utils/useRefreshRef.ts";
import { SafetyControlSurveyForm } from "./SafetyControlSurveyForm.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { faCheck, faFileSignature, faPrint } from "@fortawesome/pro-regular-svg-icons";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { FillSafetyControlSurveyView } from "./FillSafetyControlSurveyView.tsx";
import { downloadFile } from "src/utils/fileDownloading.ts";
import { getSafetyControlSurveyStateLabel } from "src/api/generated/erp/db/types/enums/safetyControlSurveyState.ts";
import {
	getSafetyControlSurveyTypeLabel,
	SafetyControlSurveyTypeValues,
} from "src/api/generated/erp/db/types/enums/safetyControlSurveyType.ts";
import { genericNullableValue } from "src/utils/genericNullableValue.ts";
import { DocumentsOfObjectButton } from "src/components/views/documents/objectDocuments/DocumentsOfObjectButton.tsx";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";
import { AavoSelectField } from "../../../common/inputFields/AavoSelectField.tsx";
import { nullableAavoObjectRef } from "src/utils/aavoObjectRefUtils.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface SafetyControlSurveysDataGridProps {
	showActiveOnly: boolean;
	onlySafetyControlSurveyToShowId?: number;
	onSelectionChanged: (survey: SafetyControlSurveyView | undefined) => void;
	refreshRef?: RefreshableElementRef;
}

export const SafetyControlSurveysDataGrid = ({
	showActiveOnly,
	onlySafetyControlSurveyToShowId,
	onSelectionChanged,
	refreshRef,
}: SafetyControlSurveysDataGridProps) => {
	const { openDialog } = useGenericDialog();

	return (
		<ClientSideDataGridModel
			fetchData={async ({ searchQuery, surveyType }) => {
				if (onlySafetyControlSurveyToShowId != null) {
					const survey = await SafetyControlSurveyQueryApi.getSurvey({
						safetyControlSurveyId: onlySafetyControlSurveyToShowId,
					});
					const isActive = survey.safetyControlSurveyState !== "CLOSED";
					return isActive === showActiveOnly ? [survey] : [];
				}

				return SafetyControlSurveyQueryApi.getSurveys({
					states: showActiveOnly ? ["INITIAL", "STARTED"] : ["CLOSED"],
					surveyType: surveyType,
					searchQuery: searchQuery,
				});
			}}
			getRows={(data) => data}
			initialParams={{
				searchQuery: "",
				surveyType: genericNullableValue<(typeof SafetyControlSurveyTypeValues)[number]>(),
			}}
			getRowId={(row) => row.safetyControlSurveyId}
			gridId={showActiveOnly ? "8D9933772E92B2D5" : "905F1B8A179A0835"}
			onSelectionChanged={(rows) => onSelectionChanged(rows[0])}
			selectFirstRowOnLoad={onlySafetyControlSurveyToShowId != null}
			refreshRef={refreshRef}
			render={({ dataGridProps, refreshData, currentParams, onlySelectedRow }) => (
				<FormCrudDataGrid<SafetyControlSurveyView>
					columns={[
						enumColumn({
							field: "safetyControlType",
							headerName: i18n.t("type"),
							enumLabels: getSafetyControlSurveyTypeLabel,
							width: 120,
						}),
						textColumn({ field: "name", headerName: i18n.t("name"), width: 150 }),
						textColumn({ field: "description", headerName: i18n.t("description"), width: 200 }),
						textColumn({ field: "siteName", headerName: i18n.t("site"), width: 120 }),
						textColumn({ field: "teamName", headerName: i18n.t("team"), width: 120 }),
						textColumn({ field: "participants", headerName: i18n.t("participants"), width: 150 }),
						enumColumn({
							field: "safetyControlSurveyState",
							headerName: i18n.t("state"),
							enumLabels: getSafetyControlSurveyStateLabel,
							width: 100,
						}),
						integerColumn({
							field: "safetyControlSurveyResult",
							headerName: i18n.t("result") + " (%)",
							width: 100,
						}),
						textColumn({
							field: "responsiblePersonName",
							headerName: i18n.t("responsible_person"),
							width: 150,
						}),
						dateTimeColumn({ field: "createdAt", headerName: i18n.t("created_at"), width: 150 }),
						textColumn({ field: "createdByUserName", headerName: i18n.t("created_by"), width: 120 }),
						dateTimeColumn({ field: "startedAt", headerName: i18n.t("started_at"), width: 150 }),
						dateTimeColumn({ field: "closedAt", headerName: i18n.t("closed_at"), width: 150 }),
					]}
					form={{
						addRowEnabled: showActiveOnly,
						editEnabled: true,
						dialogSize: "md",
						dialogTitle: i18n.t("survey"),
						agentContext: (row) => ({
							key: defineAgentUiContextKey("erp.safetyControl.survey.formDialog"),
							data: {
								safetyControlSurveyId: row?.safetyControlSurveyId ?? null,
							},
						}),
						component: ({ row, onCompleted, onFormEdited }) => (
							<SafetyControlSurveyForm
								survey={row}
								onCompleted={onCompleted}
								onFormEdited={onFormEdited}
							/>
						),
					}}
					remove={
						showActiveOnly ?
							{
								type: "enabled",
								action: async ({ items }) => {
									for (const item of items) {
										await SafetyControlSurveyEditApi.delete_({
											safetyControlSurveyId: item.safetyControlSurveyId,
										});
									}
								},
							}
						:	undefined
					}
					actionBarComponents={
						<>
							<DocumentsOfObjectButton
								objectRef={nullableAavoObjectRef(
									"SAFETY_CONTROL_SURVEY",
									onlySelectedRow?.safetyControlSurveyId,
								)}
							/>
							<AavoTextField
								label={i18n.t("search")}
								onSubmit={(value) => refreshData({ searchQuery: value })}
							/>
							<AavoSelectField
								label={i18n.t("type")}
								value={currentParams.surveyType}
								options={SafetyControlSurveyTypeValues}
								getOptionKey={(o) => o}
								getOptionLabel={(o) => getSafetyControlSurveyTypeLabel(o)}
								onChange={(value) => refreshData({ surveyType: value })}
							/>
							{showActiveOnly && (
								<AsyncButton
									key="fill"
									label={i18n.t("fill")}
									icon={faFileSignature}
									variant={"contained"}
									disabled={!onlySelectedRow || onlySelectedRow.closedAt != null}
									onClick={async () => {
										await SafetyControlSurveyActionApi.startSurvey({
											safetyControlSurveyId: onlySelectedRow!.safetyControlSurveyId,
										});
										openDialog(({ closeDialog }) => ({
											title: i18n.t("fill_survey"),
											size: "lg",
											agentContext: {
												key: defineAgentUiContextKey("erp.safetyControl.fillSurvey.dialog"),
												data: {
													safetyControlSurveyId: onlySelectedRow!.safetyControlSurveyId,
												},
												pathSegments: [i18n.t("fill_survey")],
											},
											content: (
												<FillSafetyControlSurveyView
													safetyControlSurveyId={onlySelectedRow!.safetyControlSurveyId}
													onClose={() => {
														closeDialog();
														refreshData();
													}}
												/>
											),
										}));
									}}
								/>
							)}
							{showActiveOnly && (
								<AsyncButton
									key="close"
									label={i18n.t("close")}
									icon={faCheck}
									variant={"outlined"}
									disabled={
										!onlySelectedRow || onlySelectedRow.safetyControlSurveyState !== "STARTED"
									}
									onClick={async () => {
										await SafetyControlSurveyActionApi.closeSurvey({
											safetyControlSurveyId: onlySelectedRow!.safetyControlSurveyId,
										});
										await refreshData();
									}}
								/>
							)}
							{!showActiveOnly && (
								<AsyncButton
									key="print"
									label={i18n.t("print")}
									icon={faPrint}
									disabled={!onlySelectedRow}
									onClick={async () => {
										const fileHandle = await SafetyControlSurveyReportApi.generateReport({
											safetyControlSurveyId: onlySelectedRow!.safetyControlSurveyId,
										});
										downloadFile(fileHandle);
									}}
								/>
							)}
						</>
					}
					agentContext={{
						key: defineAgentUiContextKey("erp.safetyControl.surveys.grid"),
						data: { showActiveOnly },
						rowFields: ["safetyControlSurveyId", "siteId", "safetyControlType", "safetyControlSurveyState"],
						selectedRowPathSegment: (row) => row.name,
						selectedRowsPathSegment: (count) => i18n.t("rows_selected_count", { count }),
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);
};
